import { useEffect, useRef, useState } from "react";

import { api, type IngestStatus } from "../lib/api";

interface Props {
  onFirstSuccess: () => void;
}

/**
 * Top-of-map banner that polls GET /api/stations/ingest-status every 2 sec and
 * surfaces the current state. Covers both the first-boot autoload and operator
 * uploads. Shows a progress bar with ETA so the user knows what to expect.
 *
 * Hides itself when state=idle. On "done" it flashes a success line for 5 sec,
 * then disappears.
 */
export function IngestBanner({ onFirstSuccess }: Props) {
  const [s, setS] = useState<IngestStatus | null>(null);
  const pollRef = useRef<number | null>(null);
  const lastStateRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const status = await api.ingestStatus();
        if (cancelled) return;
        setS(status);
        if (lastStateRef.current === "running" && status.state === "done") {
          onFirstSuccess();
          setTimeout(() => setS({ ...status, state: "idle" as const }), 5000);
        }
        lastStateRef.current = status.state;
      } catch {
        /* API may not be up yet during first boot — silently retry */
      }
    };
    tick();
    pollRef.current = window.setInterval(tick, 2000);
    return () => {
      cancelled = true;
      if (pollRef.current) window.clearInterval(pollRef.current);
    };
  }, [onFirstSuccess]);

  if (!s || s.state === "idle") return null;

  if (s.state === "failed") {
    return (
      <div style={{ ...banner, background: "#5a1a1a", color: "#fee" }}>
        Stations ingest failed: {s.error ?? "unknown error"}
      </div>
    );
  }

  if (s.state === "done") {
    return (
      <div style={{ ...banner, background: "#1a4a1a", color: "#dfd" }}>
        ✓ Stations ingested: {s.processed} rows
        {" "}
        <span style={tag}>{s.derived_agl_ok} with DEM-derived AGL</span>
        {s.derived_agl_fallback > 0 && (
          <span style={{ ...tag, background: "#553" }}>{s.derived_agl_fallback} fallback</span>
        )}
      </div>
    );
  }

  // state === "running"
  const pct = s.total ? Math.min(100, Math.round((s.processed / s.total) * 100)) : 0;
  const sourceLabel = s.source === "autoload" ? "Loading stations (first boot)" : "Ingesting uploaded CSV";
  const remaining = s.eta_sec ? fmtDuration(s.eta_sec) : (s.total ? "calculating…" : "starting…");

  return (
    <div style={banner}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
        <span style={{ fontWeight: 600 }}>⟳ {sourceLabel}</span>
        <span style={{ color: "#aac" }}>
          {s.processed}{s.total ? ` / ${s.total}` : ""} stations
          {" · "}
          <span style={{ color: "#cfc" }}>{pct}%</span>
          {" · ETA "}<span style={{ color: "#fe8" }}>{remaining}</span>
        </span>
      </div>
      <div style={bar}>
        <div style={{ ...barFill, width: `${pct}%` }} />
      </div>
      <div style={{ fontSize: 11, color: "#aaa", marginTop: 6 }}>
        Each station requires a ~1 second DEM sample. This takes ~8 min for a full ~500-station CSV. Safe to leave this page open; map markers will refresh automatically when done.
      </div>
    </div>
  );
}

function fmtDuration(sec: number): string {
  if (sec < 60) return `${Math.round(sec)}s`;
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return `${m}m ${s}s`;
}

const banner: React.CSSProperties = {
  position: "absolute",
  top: 0, left: 0, right: 320,
  background: "#1a2a4a",
  color: "#fff",
  padding: "10px 16px",
  zIndex: 20,
  fontSize: 13,
  borderBottom: "1px solid #333",
};
const bar: React.CSSProperties = {
  width: "100%", height: 4, marginTop: 6, background: "#0a1a33", borderRadius: 2, overflow: "hidden",
};
const barFill: React.CSSProperties = {
  height: "100%", background: "#3a7bd5", transition: "width 400ms",
};
const tag: React.CSSProperties = {
  marginLeft: 6, fontSize: 11, background: "#334", color: "#fff",
  padding: "1px 6px", borderRadius: 3,
};
